/**
 * Public page shown when login is refused with STORAGE_BLOCK_ACTIVE.
 *
 * iter-217 — User re-enters password, we re-validate, show them the overage
 * breakdown and a Razorpay checkout button. No session is created; they're
 * still blocked until payment lands. Once paid they get redirected to login.
 */
import React, { useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { AlertTriangle, Lock } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Logo } from '../components/Logo';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const paiseToRupees = (p) => (Number(p || 0) / 100).toLocaleString('en-IN', { maximumFractionDigits: 2 });

export const StorageOverageDue = () => {
  const [params] = useSearchParams();
  const [email, setEmail] = useState(params.get('email') || '');
  const [password, setPassword] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [paying, setPaying] = useState(false);
  const navigate = useNavigate();

  const loadStatus = async (e) => {
    e?.preventDefault();
    setLoading(true);
    try {
      const { data: d } = await axios.post(`${API_URL}/api/storage/public/status`, { email, password });
      if (!d.blocked) {
        toast.success('Account is clear, you can log in');
        navigate('/login');
        return;
      }
      setData(d);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not verify credentials');
    } finally {
      setLoading(false);
    }
  };

  const payNow = async () => {
    if (!window.Razorpay) {
      toast.error('Payment library unavailable, please refresh');
      return;
    }
    setPaying(true);
    try {
      const { data: order } = await axios.post(
        `${API_URL}/api/storage/pay-mid-cycle`,
        {},
        { headers: { Authorization: `Bearer ${data.token}` } },
      );
      const rzp = new window.Razorpay({
        key: order.key_id,
        amount: order.amount,
        currency: order.currency,
        name: 'EquiTrack',
        description: 'Storage overage (mid-cycle)',
        order_id: order.order_id,
        prefill: { email: data.email, name: data.name },
        handler: async (resp) => {
          try {
            await axios.post(
              `${API_URL}/api/payments/verify-payment`,
              {
                razorpay_order_id: resp.razorpay_order_id,
                razorpay_payment_id: resp.razorpay_payment_id,
                razorpay_signature: resp.razorpay_signature,
              },
              { headers: { Authorization: `Bearer ${data.token}` } },
            );
            toast.success('Payment confirmed — redirecting to login');
            setTimeout(() => navigate('/login'), 1200);
          } catch (e) {
            toast.error('Payment captured but verification failed, please contact support');
          }
        },
        modal: { ondismiss: () => setPaying(false) },
        theme: { color: '#1B4332' },
      });
      rzp.open();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not initiate payment');
      setPaying(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6" style={{ backgroundColor: '#FAFAF7' }}>
      <div className="w-full max-w-xl rounded-2xl p-8" style={{ backgroundColor: '#FFFFFF', border: '1px solid #E5E5DF' }} data-testid="storage-overage-due-card">
        <Link to="/" className="inline-flex items-center mb-6">
          <Logo size="lg" />
        </Link>

        <div className="flex items-start gap-3 mb-4 p-3 rounded" style={{ backgroundColor: '#FEF2F2', border: '1px solid #FCA5A5' }}>
          <AlertTriangle className="h-5 w-5 flex-shrink-0 mt-0.5" style={{ color: '#B91C1C' }} />
          <div>
            <p className="font-medium text-sm" style={{ color: '#991B1B' }}>Login paused</p>
            <p className="text-xs mt-1" style={{ color: '#991B1B' }}>
              Your storage usage has exceeded the quota you've paid for this cycle and the 3-day grace period has elapsed. Settle the overage below to restore full access immediately.
            </p>
          </div>
        </div>

        {!data ? (
          <form onSubmit={loadStatus} className="space-y-4" data-testid="verify-identity-form">
            <p className="text-sm" style={{ color: '#44403C' }}>
              Please re-enter your credentials to view the overage amount and pay.
            </p>
            <div>
              <Label htmlFor="email" className="text-xs" style={{ color: '#57534E' }}>Email</Label>
              <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="overage-email-input" />
            </div>
            <div>
              <Label htmlFor="password" className="text-xs" style={{ color: '#57534E' }}>Password</Label>
              <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required data-testid="overage-password-input" />
            </div>
            <Button type="submit" disabled={loading} className="w-full" style={{ backgroundColor: '#1B4332', color: '#F7F7F5' }} data-testid="overage-verify-btn">
              <Lock className="h-4 w-4 mr-2" /> {loading ? 'Verifying…' : 'Verify & view overage'}
            </Button>
          </form>
        ) : (
          <div data-testid="overage-pay-section">
            <h2 className="text-lg font-medium mb-2" style={{ color: '#1C1917', fontFamily: 'Work Sans' }}>Settle storage overage</h2>
            <table className="w-full text-sm mb-4">
              <tbody>
                <tr>
                  <td className="py-2" style={{ color: '#57534E' }}>Current usage</td>
                  <td className="py-2 text-right" style={{ color: '#1C1917' }}>{data.used_mb.toFixed(1)} MB</td>
                </tr>
                <tr>
                  <td className="py-2" style={{ color: '#57534E' }}>Paid tier this cycle</td>
                  <td className="py-2 text-right" style={{ color: '#1C1917' }}>{data.paid_tier_mb.toFixed(0)} MB</td>
                </tr>
                <tr>
                  <td className="py-2" style={{ color: '#57534E' }}>Overage</td>
                  <td className="py-2 text-right" style={{ color: '#991B1B' }}>+ {data.overage.overage_mb.toFixed(1)} MB ({data.overage.blocks} block)</td>
                </tr>
                <tr style={{ borderTop: '1px solid #E5E7EB' }}>
                  <td className="py-2 font-medium" style={{ color: '#1C1917' }}>Due now (1 month)</td>
                  <td className="py-2 text-right font-medium" style={{ color: '#1B4332' }}>₹{paiseToRupees(data.overage.monthly_charge_paise)}</td>
                </tr>
              </tbody>
            </table>
            <p className="text-xs mb-4" style={{ color: '#78716C' }}>
              Pay one month's overage now to restore access immediately. The remaining {Math.max(0, data.overage.remaining_months - 1)} month(s) will auto-debit via your Razorpay mandate each cycle until renewal. Discounts on long plans do <strong>not</strong> apply to storage overage.
            </p>
            <Button onClick={payNow} disabled={paying} className="w-full" style={{ backgroundColor: '#1B4332', color: '#F7F7F5' }} data-testid="overage-pay-now-btn">
              {paying ? 'Opening Razorpay…' : `Pay ₹${paiseToRupees(data.overage.monthly_charge_paise)} & restore access`}
            </Button>
            <p className="text-[11px] text-center mt-3" style={{ color: '#78716C' }}>
              Need help? Email us at support@equitrack.in
            </p>
          </div>
        )}
      </div>
    </div>
  );
};
